/**
 * Tasas (Configuración): BCV y tasa 2. Los campos vienen con las tasas vigentes y el factor (tasa 2 ÷ BCV) se
 * recalcula mientras se escribe, sin esperar a registrar. «Traer la BCV del día» la registra y la deja a la vista en
 * su campo (pedido del 11-10-2026).
 */
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { fechaCaracas, formatearTasa, Money, tasaActualizarInput, type BcvTraidaDTO, type TasaActualizarInput, type TasaDTO } from '@mg/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, RefreshCw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Input } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

/** Lo escrito en un campo de tasa, si es un número mayor que cero. */
function tasaEscrita(texto: string | undefined): Money | null {
  const n = normalizarDecimal(texto ?? '');
  return /^\d+(\.\d+)?$/.test(n) && /[1-9]/.test(n) ? Money.parse(n) : null;
}

const validar = zodResolver(tasaActualizarInput);

/** Día (Caracas, AAAA-MM-DD) de la fecha que da la fuente de la BCV, o null si no la da o no se entiende. */
function diaDeLaFuente(fecha: string | null): string | null {
  if (!fecha) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
  const d = new Date(fecha);
  return Number.isNaN(d.getTime()) ? null : fechaCaracas(d);
}
const diaEnPantalla = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}/${dia.slice(0, 4)}`;

/** Lo que pasó con la última consulta de la BCV: se queda en pantalla hasta la siguiente acción. */
type Consulta = { ok: true; t: BcvTraidaDTO } | { ok: false; mensaje: string } | null;

export function TasasCard() {
  const qc = useQueryClient();
  const historial = useQuery({ queryKey: claves.tasasHistorial, queryFn: () => get<TasaDTO[]>('/tasas/historial') });
  const vigente = historial.data?.[0];
  const invalidarTasas = () => qc.invalidateQueries({ queryKey: ['tasas'] });
  const [consulta, setConsulta] = useState<Consulta>(null);

  const formTasa = useForm<TasaActualizarInput>({
    // En pantalla se escribe y se lee con coma («875,65»); al validar y enviar va el decimal canónico
    resolver: (valores, contexto, opciones) => validar({ bcv: normalizarDecimal(valores.bcv ?? ''), paralela: normalizarDecimal(valores.paralela ?? '') }, contexto, opciones),
    defaultValues: { bcv: '', paralela: '' },
    // Los campos traen las tasas vigentes y se corrige encima; lo que ya se está escribiendo no se pisa
    ...(vigente ? { values: { bcv: formatearTasa(vigente.bcv), paralela: formatearTasa(vigente.paralela) } } : {}),
    resetOptions: { keepDirtyValues: true },
  });
  const [bcvEscrita, paralelaEscrita] = useWatch({ control: formTasa.control, name: ['bcv', 'paralela'] });
  const bcv = tasaEscrita(bcvEscrita);
  const paralela = tasaEscrita(paralelaEscrita);
  // Factor EN VIVO con lo que hay en los campos: la misma cuenta del servidor (tasa 2 ÷ BCV, 8 decimales)
  const factor = bcv && paralela ? Money.parse(paralela.div(bcv).toDB()) : null;
  const cambia = !!factor && !!vigente && !factor.eq(Money.parse(vigente.factor_divisa));

  const actualizar = useMutation({
    mutationFn: (d: TasaActualizarInput) => post<TasaDTO>('/tasas', d),
    onSuccess: async (t) => {
      setConsulta(null);
      toast.success('Tasa actualizada', { description: `BCV ${formatearTasa(t.bcv)} · Paralela ${formatearTasa(t.paralela)}. Los documentos emitidos no cambian.` });
      formTasa.reset({ bcv: formatearTasa(t.bcv), paralela: formatearTasa(t.paralela) });
      await invalidarTasas();
    },
    onError: (e) => toast.error('No se pudo actualizar', { description: mensajeDeError(e) }),
  });
  const sincronizar = useMutation({
    mutationFn: () => post<BcvTraidaDTO>('/tasas/sincronizar'),
    onMutate: () => setConsulta(null),
    onSuccess: async (t) => {
      setConsulta({ ok: true, t });
      // La tasa traída queda a la vista en su campo, aunque ahí ya se hubiera escrito otra. La tasa 2 que se esté
      // escribiendo no se toca
      formTasa.resetField('bcv', { defaultValue: formatearTasa(t.bcv) });
      await invalidarTasas();
    },
    // Si la consulta falla NO se toca ningún campo ni se da nada por actualizado: se dice qué pasó y qué sigue rigiendo.
    // La BCV se puede escribir a mano: el cursor queda en su campo, con lo que hay seleccionado para escribir encima
    onError: (e) => {
      setConsulta({ ok: false, mensaje: mensajeDeError(e) });
      formTasa.setFocus('bcv', { shouldSelect: true });
    },
  });
  const diaFuente = consulta?.ok ? diaDeLaFuente(consulta.t.fecha_bcv) : null;
  /** La última consulta falló: la BCV se escribe a mano. */
  const fallo = !!consulta && !consulta.ok;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-1"><CardTitle>Tasas</CardTitle><AyudaVentana tema="configuracion.tasas" /></div>
        <CardDescription>
          Los precios en bolívares salen de la <b>tasa 2</b> (precio en $ × tasa 2); el "$ a tasa BCV" es Bs ÷ BCV. El factor se calcula solo (tasa 2 ÷ BCV). Cada actualización crea una fila en el histórico; los documentos emitidos conservan sus tasas. Escribe con coma decimal: 855,66.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="grid grid-cols-3 gap-3" onSubmit={formTasa.handleSubmit((d) => actualizar.mutate(d))} noValidate data-testid="form-tasa">
          <Campo etiqueta="BCV (Bs/$) · de internet o a mano" htmlFor="bcv" error={formTasa.formState.errors.bcv?.message} {...(fallo ? { ayuda: 'Escríbela aquí a mano y registra.' } : {})}>
            <Input id="bcv" inputMode="decimal" placeholder="Ej. 855,66" {...formTasa.register('bcv')} className={fallo ? 'border-amber-500 ring-2 ring-amber-200' : undefined} data-testid="tasa-bcv" data-a-mano={fallo ? 'si' : 'no'} />
          </Campo>
          <Campo etiqueta="Tasa 2 / Binance (Bs/$) · la escribe usted" htmlFor="paralela" error={formTasa.formState.errors.paralela?.message}>
            <Input id="paralela" inputMode="decimal" placeholder="Ej. 947,89" {...formTasa.register('paralela')} data-testid="tasa-paralela" />
          </Campo>
          <Campo etiqueta="Factor (calculado)" htmlFor="factor" ayuda={cambia ? `Vigente: ${formatearTasa(vigente.factor_divisa)}. Este rige al registrar.` : 'Tasa 2 ÷ BCV'}>
            <Input id="factor" readOnly value={factor ? formatearTasa(factor) : '—'} className={cambia ? 'bg-amber-50 font-semibold' : 'bg-muted'} data-testid="tasa-factor" data-cambia={cambia ? 'si' : 'no'} />
          </Campo>
          <div className="col-span-3 flex gap-2">
            <Button type="submit" cargando={actualizar.isPending}>
              Registrar tasas
            </Button>
            <Button type="button" variant="outline" cargando={sincronizar.isPending} onClick={() => sincronizar.mutate()} data-testid="btn-sincronizar-bcv">
              <RefreshCw /> Traer la BCV del día (internet)
            </Button>
          </div>
          {consulta?.ok ? (
            <div className="col-span-3 flex items-start gap-2 rounded-lg border border-mg-verde/40 bg-mg-verde-claro px-3 py-2 text-sm text-mg-verde-oscuro" role="status" data-testid="bcv-consulta-ok" data-registrada={consulta.t.registrada ? 'si' : 'no'}>
              <CircleCheck className="mt-0.5 size-4 shrink-0" />
              <span>
                {consulta.t.registrada ? (
                  <><b>BCV traída de internet: Bs {formatearTasa(consulta.t.bcv)} por $.</b> Quedó registrada y ya rige (antes: {formatearTasa(consulta.t.bcv_anterior)}). La tasa 2 no cambió.</>
                ) : (
                  <><b>La BCV no cambió: sigue en Bs {formatearTasa(consulta.t.bcv)} por $.</b> La fuente dio la misma tasa que ya regía y no se registró nada.</>
                )}{' '}
                {diaFuente ? `Fecha que da la fuente: ${diaEnPantalla(diaFuente)}.` : 'La fuente no dice de qué fecha es.'}
                {diaFuente && diaFuente < fechaCaracas(new Date()) ? ' No es de hoy: es la última publicada (el BCV no publica fines de semana ni feriados).' : ''}
              </span>
            </div>
          ) : consulta ? (
            <div className="col-span-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert" data-testid="bcv-consulta-error">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              <span>
                <b>No se pudo traer la BCV. No se cambió nada.</b> {consulta.mensaje}.{' '}
                {vigente ? `Sigue rigiendo la BCV de Bs ${formatearTasa(vigente.bcv)}, registrada el ${formatearFechaHora(vigente.vigente_desde)}. ` : ''}
                <b>Puedes ponerla a mano:</b> escribe la BCV de hoy en el campo BCV, que quedó listo para escribir, y pulsa Registrar tasas. Queda registrada como «manual».
              </span>
            </div>
          ) : null}
        </form>
        <EstadoQuery cargando={historial.isLoading} error={historial.error} datos={historial.data} reintentar={() => void historial.refetch()}>
          {(lista) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vigente desde</TableHead>
                  <TableHead className="text-right">BCV</TableHead>
                  <TableHead className="text-right">Paralela</TableHead>
                  <TableHead className="text-right">Factor</TableHead>
                  <TableHead>Fuente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((t, i) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      {formatearFechaHora(t.vigente_desde)} {i === 0 ? <Badge variant="ok">vigente</Badge> : null}
                    </TableCell>
                    <TableCell className="text-right num">{formatearTasa(t.bcv)}</TableCell>
                    <TableCell className="text-right num">{formatearTasa(t.paralela)}</TableCell>
                    <TableCell className="text-right num">{formatearTasa(t.factor_divisa)}</TableCell>
                    <TableCell>
                      <Badge variant={t.fuente === 'dolarapi' ? 'info' : 'neutro'}>{t.fuente === 'dolarapi' ? 'internet' : t.fuente === 'seed' ? 'inicial' : t.fuente}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </EstadoQuery>
      </CardContent>
    </Card>
  );
}
